
import { motion } from "framer-motion"
import { ArrowUpRight, Gamepad2 } from "lucide-react"
import type { Game } from "@/lib/games"

interface GameCardProps {
  game: Game
  index: number
}

export function GameCard({ game, index }: GameCardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      viewport={{ once: true }}
      className="group relative block h-full"
    >
      <a
        href={game.link}
        target="_blank"
        rel="noopener noreferrer"
        className="block h-full"
      >
        <div className="relative h-full overflow-hidden rounded-2xl border border-white/10 bg-card transition-all duration-300 hover:border-primary/50 hover:shadow-2xl hover:shadow-primary/20">
          {/* Image Container */}
          <div className="relative w-full overflow-hidden" style={{ aspectRatio: "1080 / 788" }}>
          <img
              src={game.image || "/placeholder.svg"}
              alt={game.title}
              className="object-cover w-full h-full transition-transform duration-500 group-hover:scale-110"
              style={{ objectFit: 'cover', width: '100%', height: '100%' }}
              loading="lazy"
            />
            {/* Overlay Gradient */}
            <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/20 to-transparent opacity-80 transition-opacity duration-300 group-hover:opacity-60" />

            {/* Floating Badge */}
            <div className="absolute top-3 right-3 translate-y-[-10px] opacity-0 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100">
              <div className="flex items-center gap-1 rounded-full bg-primary/90 px-3 py-1 text-xs font-medium text-white backdrop-blur-md">
                <span>Play Now</span>
                <ArrowUpRight className="h-3 w-3" />
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="relative p-5">
            <div className="mb-3 flex flex-wrap gap-2">
              {game.tags.map((tag) => (
                <span
                  key={tag}
                  className="rounded-md bg-secondary/50 px-2 py-1 text-[10px] font-medium uppercase tracking-wider text-secondary-foreground/80"
                >
                  {tag}
                </span>
              ))}
            </div>

            <h3 className="mb-2 text-xl font-bold tracking-tight text-foreground transition-colors group-hover:text-primary">
              {game.title}
            </h3>

            <p className="mb-4 line-clamp-2 text-sm text-muted-foreground">{game.description}</p>

            {game.developer && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground/60">
                <Gamepad2 className="h-3 w-3" />
                <span>{game.developer}</span>
              </div>
            )}
          </div>
        </div>
      </a>
    </motion.div>
  )
}

